import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerProps,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useToken,
} from '@chakra-ui/react';
import { useFetchAfeDetailsQuery } from 'api';
import { PostWorkOrderTransactionAccountRequest } from 'api/bindings/PostWorkOrderTransactionAccountRequest';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import { WorkOrderTransactionAccount } from 'api/bindings/WorkOrderTransactionAccount';
import Decimal from 'decimal.js';
import {
  AppInput,
  AutoComplete,
  CurrencyLabel,
  FormButtonGroup,
  MultiBar,
} from 'features/components';
import { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

interface Form {
  _id: number;
  amount: string;
}
interface Row extends PostWorkOrderTransactionAccountRequest {}

interface Option {
  _id: number;
  name: string;
  subAccountId: string;
  subAccount: SubAccountResponse;
  totalBudget: string;
  pendingBudget: string;
  totalContingency: string;
  pendingContingency: string;
  totalCommitment: string;
  pendingCommitment: string;
  totalInvoice: string;
  pendingInvoice: string;
}
export interface WorkOrderRevisionAccountDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  afeId: string;
  row: Row;
  onSubmit: (f: Row) => void;
}

export const WorkOrderRevisionAccountDrawer = (
  props: WorkOrderRevisionAccountDrawerProps
) => {
  const { afeId, row, ...others } = props;

  const { data: afe } = useFetchAfeDetailsQuery(afeId, { skip: !afeId });
  const options = useMemo(() => {
    let i = 0;
    return afe?.eacLineItems.reduce((sum, n) => {
      return [
        ...sum,
        { _id: i++, name: n.subAccount.fullSubAccountName, ...n },
      ];
    }, [] as Option[]);
  }, [afe?.eacLineItems]);

  const preSelectedOptionId = useMemo(
    () =>
      (row.subAccountId &&
        options?.find((x) => x.subAccountId === row.subAccountId)?._id) ||
      undefined,
    [options, row.subAccountId]
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      _id: preSelectedOptionId,
      amount: row.amount,
    },
  });

  const currentOptionId = form.watch('_id');
  const amount = form.watch('amount');

  const currentOption = useMemo(
    () => options?.find((x) => x._id === currentOptionId),
    [currentOptionId, options]
  );

  const net = useMemo(() => {
    const total = new Decimal(currentOption?.totalBudget || 0).add(
      currentOption?.totalContingency || 0
    );
    return total
      .sub(currentOption?.totalCommitment || 0)
      .sub(currentOption?.pendingCommitment || 0)
      .sub(amount)
      .toNumber();
  }, [
    amount,
    currentOption?.pendingCommitment,
    currentOption?.totalBudget,
    currentOption?.totalCommitment,
    currentOption?.totalContingency,
  ]);

  const onSubmit = useCallback(
    (f: Form) => {
      if (!currentOption) return;
      props.onSubmit({
        ...row,
        subAccountId: currentOption.subAccountId,
        amount: f.amount,
      });
      props.onClose();
    },
    [currentOption, props, row]
  );
  return (
    <FormProvider {...form}>
      <Drawer {...others} size="xl">
        <DrawerCloseButton />
        <DrawerHeader>
          <Heading size="xs">Commitment Account</Heading>
        </DrawerHeader>
        <DrawerContent>
          <form>
            <DrawerBody>
              <Stack spacing={[2, 5]}>
                <FormControl>
                  <FormLabel htmlFor="id">Account:</FormLabel>
                  <Controller
                    control={form.control}
                    name="_id"
                    rules={{ min: 0 }}
                    render={(p) => (
                      <AutoComplete
                        id="id"
                        items={options}
                        valueKey={(x) => x._id}
                        textKey={(x) => x.name}
                        {...p.field}
                      />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="amount">Amount:</FormLabel>
                  <Controller
                    control={form.control}
                    name="amount"
                    rules={{ min: 0 }}
                    render={(p) => <AppInput mode="currency" {...p.field} />}
                  />
                </FormControl>
                <Flex w="100%">
                  {/* <MultiBar elements={data.progressElements} /> */}
                  <MultiBar
                    max={[
                      currentOption?.totalBudget,
                      currentOption?.totalContingency,
                    ]}
                    values={[
                      currentOption?.totalCommitment,
                      currentOption?.pendingCommitment,
                      amount,
                    ]}
                  />
                </Flex>
                <SimpleGrid gap={[2, 4]} columns={2}>
                  <Text as="b">Budget:</Text>
                  <CurrencyLabel value={currentOption?.totalBudget} />
                  <Text as="b">Contingency:</Text>
                  <CurrencyLabel value={currentOption?.totalContingency} />
                  <Text as="b">Total:</Text>
                  <CurrencyLabel
                    value={[
                      currentOption?.totalBudget,
                      currentOption?.totalContingency,
                    ]}
                  />
                  <Divider />
                  <Divider />
                  <Text as="b">Commitment:</Text>
                  <CurrencyLabel value={currentOption?.totalCommitment} />
                  <Text as="b">Commitment (Pending):</Text>
                  <CurrencyLabel value={currentOption?.pendingCommitment} />
                  <Text as="b">Amount:</Text>
                  <CurrencyLabel value={amount} />
                  <Text as="b">Remaining:</Text>
                  <CurrencyLabel value={net} />
                </SimpleGrid>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onCancel={props.onClose}
                onConfirm={form.handleSubmit(onSubmit)}
                submitCaption="Save Row"
              />
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    </FormProvider>
  );
};
